import { TriangleAlert } from 'lucide-react';
import type { ButtonHTMLAttributes, CSSProperties, InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

/**
 * The few primitives the page is built from, dense by default: 28px controls, 12px text,
 * hairline borders. A touch target is still at least 28px tall, which with the gap
 * between controls clears a fingertip.
 */

export function Card({ title, actions, className, children }: { title?: ReactNode; actions?: ReactNode; className?: string; children: ReactNode }) {
  return (
    <section className={cn('min-w-0 rounded-md border border-line bg-surface', className)}>
      {title !== undefined && (
        <header className="flex min-h-8 items-center justify-between gap-2 border-b border-line px-2.5 py-1">
          <h2 className="truncate text-xs font-semibold tracking-wide text-ink-2 uppercase">{title}</h2>
          {actions}
        </header>
      )}
      {children}
    </section>
  );
}

type ButtonVariant = 'primary' | 'ghost' | 'outline';

export function Button({ variant = 'outline', className, ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant }) {
  return (
    <button
      type="button"
      className={cn(
        'inline-flex h-7 items-center justify-center gap-1.5 rounded-md px-2.5 text-xs font-medium whitespace-nowrap transition-colors',
        'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent disabled:cursor-not-allowed disabled:opacity-50',
        variant === 'primary' && 'bg-accent text-accent-ink hover:opacity-90',
        variant === 'outline' && 'border border-line bg-surface text-ink hover:bg-surface-2',
        variant === 'ghost' && 'text-ink-2 hover:bg-surface-2 hover:text-ink',
        className,
      )}
      {...props}
    />
  );
}

export function Input({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      className={cn(
        'h-7 w-full min-w-0 rounded-md border border-line bg-page px-2 text-xs text-ink tabular placeholder:text-muted',
        'focus-visible:border-accent focus-visible:outline-none',
        className,
      )}
      {...props}
    />
  );
}

/** The browser's own select, drawn like `Input`: its menu is the one a phone knows how to show. */
export function Select({ className, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      className={cn(
        'h-7 w-full min-w-0 rounded-md border border-line bg-page px-1.5 text-xs text-ink tabular disabled:text-muted',
        'focus-visible:border-accent focus-visible:outline-none',
        className,
      )}
      {...props}
    />
  );
}

export function Field({ label, hint, children, className }: { label: string; hint?: string; children: ReactNode; className?: string }) {
  return (
    <label className={cn('flex min-w-0 flex-col gap-0.5', className)}>
      <span className="text-[11px] text-ink-2">
        {label}
        {hint !== undefined && <span className="ml-1 text-muted">{hint}</span>}
      </span>
      {children}
    </label>
  );
}

export interface SegmentOption<T extends string> {
  readonly value: T;
  readonly label: string;
}

/** A radio group drawn as joined buttons. */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: ReadonlyArray<SegmentOption<T>>;
  onChange: (value: T) => void;
  label: string;
}) {
  return (
    <div role="radiogroup" aria-label={label} className="inline-flex h-7 rounded-md border border-line bg-page p-0.5">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={option.value === value}
          onClick={() => onChange(option.value)}
          className={cn('rounded-sm px-2.5 text-xs font-semibold transition-colors', option.value === value ? 'bg-accent text-accent-ink' : 'text-ink-2 hover:text-ink')}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function Spinner({ className }: { className?: string }) {
  return <span aria-hidden className={cn('inline-block size-3 animate-spin rounded-full border-2 border-line border-t-accent', className)} />;
}

export function Message({ tone = 'muted', children }: { tone?: 'muted' | 'error'; children: ReactNode }) {
  return <div className={cn('px-2.5 py-3 text-xs', tone === 'error' ? 'text-negative' : 'text-muted')}>{children}</div>;
}

/** A marker in an outcome's shape, for legends and tables, so identity never rests on colour alone. */
export function OutcomeMarker({ color, shape, size = 8 }: { color: string; shape: 'circle' | 'diamond' | 'square' | 'ring'; size?: number }) {
  const r = size / 2;
  return (
    <svg width={size} height={size} viewBox={`${-r} ${-r} ${size} ${size}`} aria-hidden className="shrink-0 overflow-visible">
      <MarkerShape color={color} shape={shape} r={r} />
    </svg>
  );
}

export function MarkerShape({ color, shape, r }: { color: string; shape: 'circle' | 'diamond' | 'square' | 'ring'; r: number }) {
  switch (shape) {
    case 'circle':
      return <circle r={r} fill={color} />;
    case 'ring':
      return <circle r={r - 0.75} fill="none" stroke={color} strokeWidth={1.5} />;
    case 'square':
      return <rect x={-r * 0.85} y={-r * 0.85} width={r * 1.7} height={r * 1.7} rx={1} fill={color} />;
    case 'diamond':
      return <path d={`M0 ${-r * 1.15} L${r * 1.15} 0 L0 ${r * 1.15} L${-r * 1.15} 0 Z`} fill={color} />;
  }
}

/**
 * A placeholder block the size of what is loading, so the page is laid out before its data
 * arrives and nothing below it jumps when it does. Hidden from assistive technology: the
 * container a skeleton stands in carries the `role="status"` that says what is loading.
 */
export function Skeleton({ className, style }: { className?: string; style?: CSSProperties }) {
  return <span aria-hidden className={cn('block animate-pulse rounded-sm bg-surface-2', className)} style={style} />;
}

/** An on/off setting, as a switch with its label: `role="switch"`, so it reads as one. */
export function Switch({
  checked,
  onChange,
  label,
  disabled,
  hideLabel,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  disabled?: boolean;
  /** For a table, whose column header already says what the switch is. */
  hideLabel?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className="group inline-flex min-h-7 items-center gap-2 rounded-md text-xs text-ink-2 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent disabled:cursor-not-allowed disabled:opacity-50"
    >
      <span className={cn('relative h-4 w-7 shrink-0 rounded-full transition-colors', checked ? 'bg-accent' : 'bg-surface-2 ring-1 ring-line ring-inset')}>
        <span className={cn('absolute top-0.5 left-0.5 size-3 rounded-full bg-surface shadow-sm transition-transform', checked && 'translate-x-3 bg-accent-ink')} />
      </span>
      <span className={cn(hideLabel === true && 'sr-only')}>{label}</span>
    </button>
  );
}

const TONE_TEXT = { positive: 'text-positive', warning: 'text-warning', negative: 'text-negative', muted: 'text-muted' } as const;
const TONE_DOT = { positive: 'bg-positive', warning: 'bg-warning', negative: 'bg-negative', muted: 'bg-muted' } as const;

/** A coloured dot and its words: the words carry the meaning, so colour never does alone. */
export function StatusDot({ tone, label, pulse }: { tone: keyof typeof TONE_TEXT; label: string; pulse?: boolean }) {
  return (
    <span className={cn('inline-flex items-center gap-1.5 text-xs font-medium whitespace-nowrap', TONE_TEXT[tone])}>
      <span className="relative flex size-2">
        {pulse === true && <span className={cn('absolute inline-flex size-full animate-ping rounded-full opacity-60', TONE_DOT[tone])} />}
        <span className={cn('relative inline-flex size-2 rounded-full', TONE_DOT[tone])} />
      </span>
      {label}
    </span>
  );
}

/** A short label beside a name: LIVE, paper, invalid keys. */
export function Badge({ tone = 'muted', children }: { tone?: 'muted' | 'negative'; children: ReactNode }) {
  return (
    <span
      className={cn(
        'inline-flex h-4.5 items-center rounded-sm px-1.5 text-[10px] font-semibold tracking-wide uppercase',
        tone === 'muted' && 'bg-surface-2 text-ink-2',
        tone === 'negative' && 'bg-negative/12 text-negative',
      )}
    >
      {children}
    </span>
  );
}

/** What went wrong, in the service's words, where the action was taken. */
export function ErrorNote({ children }: { children: ReactNode }) {
  return (
    <div role="alert" className="flex items-start gap-2 rounded-md border border-negative/30 bg-negative/10 p-2.5 text-xs text-negative">
      <TriangleAlert className="mt-px size-3.5 shrink-0" />
      <div className="min-w-0 break-words">{children}</div>
    </div>
  );
}

/** A page's title and what it is for, above its content. */
export function PageHeader({ title, description }: { title: string; description?: ReactNode }) {
  return (
    <header className="flex flex-col gap-0.5 px-0.5">
      <h1 className="text-base font-semibold tracking-tight">{title}</h1>
      {description !== undefined && <p className="text-xs text-muted">{description}</p>}
    </header>
  );
}

/**
 * A table in a card: its title on the left of the header and its action — Add — on the
 * right, the way mini-cloud lays them out. The table scrolls sideways inside the card
 * rather than the page when a narrow screen cannot fit it.
 */
export function TableCard({ title, count, action, footer, children }: { title: string; count?: number; action?: ReactNode; footer?: ReactNode; children: ReactNode }) {
  return (
    <section className="min-w-0 overflow-hidden rounded-md border border-line bg-surface">
      <header className="flex min-h-10 flex-wrap items-center justify-between gap-2 border-b border-line px-2.5 py-1.5">
        <h2 className="flex items-center gap-1.5 text-xs font-semibold tracking-wide text-ink-2 uppercase">
          {title}
          {count !== undefined && <span className="rounded-sm bg-surface-2 px-1.5 text-[10px] text-muted tabular">{count}</span>}
        </h2>
        {action}
      </header>
      <div className="overflow-x-auto scrollbar-thin">{children}</div>
      {footer}
    </section>
  );
}

export function Table({ children }: { children: ReactNode }) {
  return <table className="w-full border-collapse text-xs [&_tbody_tr:last-child_td]:border-b-0">{children}</table>;
}

export function Th({ children, className }: { children?: ReactNode; className?: string }) {
  return (
    <th className={cn('h-8 border-b border-line bg-surface-2/50 px-2.5 text-left text-[10px] font-semibold tracking-wider whitespace-nowrap text-muted uppercase', className)}>
      {children}
    </th>
  );
}

/** `title` for a figure shown rounded: the exact one, on hover. */
export function Td({ children, className, title }: { children?: ReactNode; className?: string; title?: string }) {
  return (
    <td className={cn('border-b border-line px-2.5 py-2 align-middle', className)} title={title}>
      {children}
    </td>
  );
}

export interface TabLink<T extends string> {
  readonly tab: T;
  readonly label: string;
  readonly hash: string;
  readonly count?: number;
}

/** Tabs that are links, so each can be bookmarked and the back button walks them. */
export function TabLinks<T extends string>({ tabs, current, label }: { tabs: ReadonlyArray<TabLink<T>>; current: T; label: string }) {
  return (
    <div role="tablist" aria-label={label} className="flex gap-1 border-b border-line">
      {tabs.map((entry) => {
        const active = entry.tab === current;
        return (
          <a
            key={entry.tab}
            role="tab"
            href={entry.hash}
            aria-selected={active}
            className={cn(
              '-mb-px flex h-9 items-center gap-1.5 border-b-2 px-3 text-xs font-medium transition-colors focus-visible:outline-2 focus-visible:outline-accent',
              active ? 'border-accent text-ink' : 'border-transparent text-ink-2 hover:text-ink',
            )}
          >
            {entry.label}
            {entry.count !== undefined && (
              <span className={cn('rounded-sm px-1.5 text-[10px] tabular', active ? 'bg-accent-soft text-accent' : 'bg-surface-2 text-muted')}>{entry.count}</span>
            )}
          </a>
        );
      })}
    </div>
  );
}
